import {Component, inject} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {CommonModule} from '@angular/common';
import {RouterModule} from '@angular/router';
import {MatCard, MatCardContent, MatCardHeader, MatCardTitle} from '@angular/material/card';
import {MatTableModule} from '@angular/material/table';
import {MatButton} from '@angular/material/button';
import {MatDialog} from '@angular/material/dialog';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {ContainerComponent, RowComponent, ColComponent} from '@coreui/angular';
import {Observable, of, switchMap} from 'rxjs';
import {GameService} from '../../services/game.service';
import {PlayersService} from '../../services/players.service';
import {DataAccessService} from '../../services/data-access.service';
import {AppConfigService} from '../../services/app-config.service';
import {ErrorHandlingService} from '../../services/error-handling.service';
import {PermissionsService} from '../../services/permissions.service';
import {PermissionTypes} from '../../enum/PermissionTypes';
import {UIStateEnum} from '../../enum/UIStateEnum';
import {IGameQueryResult} from '../../interfaces/IGameQueryResult';
import {IDuplicateCardFingerprintGroup} from '../../interfaces/player/IDuplicateCardFingerprintGroup';
import {IDuplicateCardFingerprintGroupPlayer} from '../../interfaces/player/IDuplicateCardFingerprintGroupPlayer';
import {IDataAccessAuthorization} from '../../interfaces/audit/IDataAccessAuthorization';
import {DataAccessReasonDialogComponent} from '../../components/dialogs/data-access-reason/data-access-reason.component';
import {ChooseYourGameComponent} from '../../components/choose-your-game/choose-your-game.component';

/**
 * Cards shared between players in a game. A Stripe card fingerprint is stable per card number, so
 * one fingerprint against two players means the same physical card is funding two accounts — the
 * signal this page exists to surface.
 *
 * The summary deliberately carries no player identifiers: it says a fingerprint is shared and by how
 * many players, and the players behind it are only fetched after the admin states a reason, which is
 * recorded as a data access authorization. That keeps the fraud check usable without turning it into
 * a player list (#1507).
 */
@Component({
  selector: 'app-duplicate-card-fingerprints',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatCardContent,
    MatTableModule,
    MatButton,
    MatProgressSpinner,
    ContainerComponent,
    RowComponent,
    ColComponent,
    ChooseYourGameComponent
  ],
  templateUrl: './duplicate-card-fingerprints.component.html',
  styleUrl: './duplicate-card-fingerprints.component.scss'
})
export class DuplicateCardFingerprintsComponent {
  /** Must match MaxDuplicateFingerprintResults on PlayerPaymentMethodAdminController. */
  private static readonly MaxResults = 500;

  private route = inject(ActivatedRoute);
  private gameService: GameService = inject(GameService);
  private playerService: PlayersService = inject(PlayersService);
  private dataAccessService: DataAccessService = inject(DataAccessService);
  private appConfigService: AppConfigService = inject(AppConfigService);
  private errorHandlingService: ErrorHandlingService = inject(ErrorHandlingService);
  private dialog: MatDialog = inject(MatDialog);
  private permissionService: PermissionsService = inject(PermissionsService);

  public uiState: UIStateEnum = UIStateEnum.ShowData;
  public uiStateForTemplate = UIStateEnum;
  public isCrossGame = false;
  public game: IGameQueryResult | null = null;

  public duplicateGroups: IDuplicateCardFingerprintGroup[] = [];
  public resultsTruncated = false;
  public maxResultsForTemplate = DuplicateCardFingerprintsComponent.MaxResults;

  public selectedFingerprint: string | null = null;
  public selectedFingerprintPlayers: IDuplicateCardFingerprintGroupPlayer[] = [];
  public playersUiState: UIStateEnum = UIStateEnum.ShowData;

  /**
   * This page is gated on ViewDuplicateCardFingerprints so it can be granted to the people who
   * investigate shared cards without granting it to every host admin — which means the persona it
   * exists for may well not hold ViewPlayerInfo. The manage-player route is guarded by that
   * permission, so linking unconditionally sends exactly that admin to /not-authorized. The email is
   * shown as plain text instead (#1507 review).
   */
  public canViewPlayerInfo = this.permissionService.userHasPermission(PermissionTypes.ViewPlayerInfo);

  public groupColumns = ['cardFingerprint', 'cardLastFourDigits', 'playerCount', 'paymentMethodCount', 'lastSeenOn', 'actions'];
  public playerColumns = ['playerEmail', 'cardLastFourDigits', 'paymentMethodCount', 'hasActivePaymentMethod', 'playerActive', 'firstSeenOn'];

  /**
   * The reason stated for this game, kept so every later drill-down can be recorded against it
   * without re-showing the dialog. One audit row per drill-down, not one per visit: each "View
   * Players" click discloses a different set of player emails, so a single row naming only the first
   * fingerprint cannot answer "who viewed player X, and why?" (#1507 review).
   */
  private statedReason: { accessReason: number; additionalDetails: string } | null = null;

  constructor() {
    this.isCrossGame = this.route.snapshot.data['isCrossGame'] === true;

    if (!this.isCrossGame) {
      this.game = this.gameService.activeGame();

      if (this.game) {
        this.loadDuplicateGroups();
      }
    }
  }

  onGameChosen(chosenGameP: IGameQueryResult): void {
    this.game = chosenGameP;
    this.clearSelection();

    // A stated reason authorizes access to ONE game's player data — the audit record carries that
    // game id. Switching games must re-prompt, or the second game's lookups are never recorded.
    this.statedReason = null;

    this.loadDuplicateGroups();
  }

  onViewPlayersClick(groupP: IDuplicateCardFingerprintGroup): void {
    if (!this.game) {
      return;
    }

    if (this.statedReason || this.appConfigService.privacyPromptsDisabled()) {
      this.loadPlayersForFingerprint(groupP.CardFingerprint);
      return;
    }

    const dialogRef = this.dialog.open(DataAccessReasonDialogComponent, {
      data: { gameId: this.game.Id },
      disableClose: true,
      width: '900px'
    });

    dialogRef.afterClosed().subscribe({
      next: (result) => {
        if (!result || !this.game) {
          return;
        }

        // The reason is held, not logged here: loadPlayersForFingerprint writes the audit row per
        // drill-down, so the second fingerprint opened is recorded as well as the first.
        this.statedReason = {
          accessReason: result.accessReason,
          additionalDetails: result.additionalDetails || ''
        };

        this.loadPlayersForFingerprint(groupP.CardFingerprint);
      },
      error: (err) => {
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });
  }

  /**
   * One record per drill-down, written BEFORE the players are fetched. The stated reason is reused so
   * there is no second dialog, but each "View Players" click discloses a different set of player
   * emails and so leaves its own record — a single row naming only the first fingerprint cannot
   * answer "who viewed player X, and why?" (#1507 review).
   *
   * Order matters: the audit row is written first and the drill-down only runs if that succeeded, so
   * a failed audit write blocks the disclosure rather than trailing it.
   */
  private logDrillDownAccess(fingerprintP: string): Observable<unknown> {
    if (!this.statedReason || !this.game) {
      return of(null);
    }

    const audit_data: IDataAccessAuthorization = {
      GameId: this.game.Id,
      AccessReason: this.statedReason.accessReason,
      AdditionalDetails: [
        this.statedReason.additionalDetails,
        'Duplicate card fingerprint drill-down.'
      ].filter(part => !!part).join(' | '),
      PlayerName: `Duplicate Card Fingerprint - ${fingerprintP}`
    };

    return this.dataAccessService.logDataAccessAuthorization(audit_data);
  }

  onClosePlayersClick(): void {
    this.clearSelection();
  }

  private clearSelection(): void {
    this.selectedFingerprint = null;
    this.selectedFingerprintPlayers = [];
  }

  private loadDuplicateGroups(): void {
    if (!this.game) {
      return;
    }

    this.uiState = UIStateEnum.ShowLoading;

    this.playerService.getDuplicateCardFingerprintsForGame(this.game.Id).subscribe({
      next: (groups) => {
        this.duplicateGroups = groups;
        this.resultsTruncated = groups.length >= DuplicateCardFingerprintsComponent.MaxResults;
        this.uiState = UIStateEnum.ShowData;
      },
      error: (err) => {
        this.uiState = UIStateEnum.ShowData;
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });
  }

  private loadPlayersForFingerprint(fingerprintP: string): void {
    if (!this.game) {
      return;
    }

    this.selectedFingerprint = fingerprintP;
    this.selectedFingerprintPlayers = [];
    this.playersUiState = UIStateEnum.ShowLoading;

    // Audit first, disclose second — see logDrillDownAccess.
    this.logDrillDownAccess(fingerprintP).pipe(
      switchMap(() => this.playerService.getPlayersForDuplicateCardFingerprint(this.game!.Id, fingerprintP))
    ).subscribe({
      next: (players) => {
        this.selectedFingerprintPlayers = players;
        this.playersUiState = UIStateEnum.ShowData;
      },
      error: (err) => {
        this.playersUiState = UIStateEnum.ShowData;
        this.errorHandlingService.displayPageLevelErrorMessage(err);
      }
    });
  }

  getPlayerManageUrl(playerIdP: string): string {
    return `/${this.game?.Id}/manage-player/${playerIdP}`;
  }
}
